<template>
    <div
        class="layout__main layout__main--no-top-padding layout__main--double"
        :class="{
            'layout__main--sidebar': isDesktop,
        }"
    >
        <BaseSection
            class="container-narrow layout__hero"
        >
            <template #breadcrumbs>
                <nav
                    v-if="condition?.parent?.uri"
                    class="link--back"
                >
                    <NuxtLink
                        class="link--back__item"
                        :to="'/'+condition.parent.uri"
                    >
                        <BaseIcon
                            class="back-button__icon dn-icon--square"
                            icon="chevron-left"
                        />

                        {{ condition.parent.title }}
                    </NuxtLink>
                </nav>
            </template>
            <template
                v-if="lottieIllustration"
                #lottie
            >
                <BaseLottieIllustration
                    :lottie-illustration="lottieIllustration"
                    size="sm"
                />
            </template>
            <template #image>
                <template v-if="condition">
                    <BaseFigure
                        v-if="condition.headerImage && condition.headerImage.length"
                        :src="condition.headerImage[0].large"
                        :alt="condition.headerImage[0].alt || ''"
                        :srcset="getSrcSet(condition.headerImage[0],'headerImage')"
                        sizes="(min-width: 1401px) 305px, (min-width: 993px) 230px, 50vw"
                    />
                </template>
                <template v-else>
                    <ContentPlaceholdersText :lines="1" />
                </template>
            </template>
            <template #title>
                <h1>
                    <template v-if="condition">
                        {{ condition.title }}
                    </template>
                    <template v-else>
                        <ContentPlaceholdersText :lines="1" />
                    </template>
                </h1>
            </template>
            <template #description>
                <template
                    v-if="condition"
                >
                    <!-- eslint-disable vue/no-v-html -->
                    <div v-html="condition.introText" />
                    <!-- eslint-enable vue/no-v-html -->
                </template>
                <template v-else>
                    <ContentPlaceholdersText :lines="3" />
                </template>
            </template>
        </BaseSection>
        <article
            class="container-narrow"
        >
            <ClientOnly>
                <TheAsideNav
                    v-if="isDesktop && condition?.sections?.length"
                    :offset="navigationHeight"
                    :sections="toValue(condition)?.sections"
                    :top-items="[{
                        title: heading,
                        id: slugify(toValue(heading) ?? '').toLowerCase()
                    }]"
                    :has-context-nav="hasContextNav"
                />
            </ClientOnly>
            <main>
                <BaseSection class="container-narrow">
                    <h4
                        v-if="heading"
                        :id="slugify(heading).toLowerCase()"
                    >
                        {{ heading }}
                    </h4>

                    <BaseQuicklinkList
                        v-if="condition?.contextualNavigation && condition.contextualNavigation?.length"
                        :links="condition.contextualNavigation.flatMap(item =>
                            item?.groupedConditions ||
                            item?.groupedStages ||
                            item?.groupedTreatments ||
                            item?.groupedPages ||
                            item?.groupedResources ||
                            []

                        ).map(t => ({
                            ...t,
                            text: t.contextTitle ?? t.title,
                            uri: t?.uri?.startsWith('http') ? t.uri : `/${t?.uri}`
                        }))"
                    />
                    <BaseQuicklinkList
                        v-else-if="condition?.linksList && condition.linksList?.length"
                        :links="condition.linksList"
                    />
                </BaseSection>

                <FlexibleSections
                    :sections="condition ? condition.sections : []"
                    :placeholder="pending ? 'overview' : undefined"
                    :accordion-plus="true"
                />

                <FlexibleSectionsLearningPaths
                    v-if="condition?.slider?.length"
                    :learning-paths="condition.slider"
                    :slider-type="'summary'"
                    :class="`container-narrow`"
                />

                <ClientOnly>
                    <BaseFeedback :id="toValue(condition)?.id" />
                </ClientOnly>

                <BaseSection
                    v-if="hasContextNav"
                    class="container-narrow"
                >
                    <TheContextNav
                        :ancestors="toValue(condition).ancestors"
                        :title="toValue(condition)?.title ?? ''"
                        :uri="toValue(condition)?.uri ?? ''"
                    />
                </BaseSection>
            </main>
        </article>
    </div>
</template>

<script setup lang="ts">
import ConditionQuery from '~/graphql/queries/Condition.graphql';
import useSrcSets from '~/utils/srcset';

import slugify from 'slugify';

const { activeSite } = useMultisite();
const {getSrcSet} = useSrcSets();

const isDesktop = useIsDesktop();

const navigationHeight = computed(() =>
    typeof document === 'undefined' ? 0 : document.querySelector('.uro-navigation')?.clientHeight || 0
);

const hasContextNav = computed(() => {
    return toValue(condition)?.ancestors?.length || toValue(condition)?.title;
});

const { variables } = useCraftGraphqlSlug();
const slug = useRoute().params.slug as string;

const response = await useLazyAsyncQuery({
    query: ConditionQuery,
    variables: {
        ...toValue(variables),
        level: slug.length
    }
});

const { craftEntry: condition, pending } = usePageQuery(
    response,
    { entryKey: 'condition', activeSite }
);

const heading = computed(() =>
    toValue(condition)?.contextualHeading?.[0]?.title
);

const lottieIllustration = ref('');
const lottieLoading = ref(false);

async function fetchAnimation(url: string) {
    const data = await $fetch<Record<string, unknown>>('/api/animations', {
        query: { url },
    });

    return JSON.stringify(data);
}

watch(() => condition?.value?.animatedIllustration?.[0]?.url, async(url) => {
    lottieIllustration.value = '';

    if (!url) {
        lottieLoading.value = false;
        return;
    }

    lottieLoading.value = true;

    try {
        lottieIllustration.value = await fetchAnimation(url);
    } catch (error) {
        if (import.meta.dev) {
            // eslint-disable-next-line no-console
            console.error('[lottie:condition] fetch failed', error);
        }
    } finally {
        lottieLoading.value = false;
    }
}, { immediate: true });

const {
    pageType,
    updatePageType
} = inject('pageType');

watch(() => condition.value, () => {
    if (!condition.value) {
        return;
    }

    switch (condition.value.__typename) {
    case 'condition_condition_Entry':
        updatePageType('condition');
        break;
    case 'condition_stage_Entry':
        updatePageType('situation');
        break;
    case 'condition_treatment_Entry':
        updatePageType('treatment');
        break;
    default:
        updatePageType('condition');
        break;
    }
}, { immediate: true });
</script>
<style lang="less" scoped>
.py-0 {
    padding-top: 0;
    padding-bottom: 0;
}

.link--back {
    margin: 0 0 var(--spacing-2xl);

    .link--back__item {
        display: inline-flex;
        align-items: center;
        font-size: var(--font-size-sm);
        font-weight: bold;
        text-decoration: none;
        color: inherit;

        &:hover {
            color: var(--color-secondary-900, var(--color-primary));
        }

        .back-button__icon {
            margin-right: 0.35rem;
        }
    }
}

.layout__main {
    background: var(--color-lightest);
}

.layout__hero {
    background: var(--hero-bg-color);
    margin-top: 0;

    padding-block: 1.875rem;

    @media @q-md-min {
        grid-column: 1 / -1 !important;  /* stylelint-disable-line declaration-no-important */
        width: 100% !important;  /* stylelint-disable-line declaration-no-important */

        padding-block: 3rem;
    }

}
</style>
